import React, { useEffect, useRef, useState } from 'react';
import useLang from '../../i18n/useLang';
import { Reveal, WordsReveal, useInView, usePrefersReducedMotion } from '../site/motion';

/*
 * The process map: one production order travels from purchasing to finance. Each step lights up with
 * its SAP object and a one-line insight. Maintenance branches off production instead of sitting on the
 * main line. The order plays through once when the map scrolls into view, pauses off screen, and stops
 * for good as soon as the visitor picks a step. Desktop is a tablist; phones get the whole list.
 */

const BRANCH = 'maintenance';
const STEP_MS = 2300;

function Dot({ active, done, branch }) {
  const tone = active
    ? 'border-grovv bg-grovv'
    : done
      ? 'border-grovv-700 bg-grovv-700'
      : `border-white/35 bg-navy-900 group-hover:border-white/70 ${branch ? 'border-dashed' : ''}`;
  return <span aria-hidden="true" className={`relative block h-5 w-5 shrink-0 rounded-full border-2 transition-colors duration-500 ${tone}`} />;
}

export default function ProcessMap() {
  const { t, isHe } = useLang();
  const p = (k) => t(`home.process.${k}`);
  const reduced = usePrefersReducedMotion();
  const raw = t('home.process.steps', { returnObjects: true });
  const steps = Array.isArray(raw) ? raw : [];
  const last = Math.max(0, steps.length - 1);

  const mapRef = useRef(null);
  const tabRefs = useRef([]);
  const inView = useInView(mapRef, { once: false, threshold: 0.3, rootMargin: '0px' });
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (reduced || !auto || !inView) return undefined;
    if (active >= last) {
      setAuto(false);
      return undefined;
    }
    const id = setTimeout(() => setActive((a) => Math.min(last, a + 1)), STEP_MS);
    return () => clearTimeout(id);
  }, [reduced, auto, inView, active, last]);

  // Column of every step on the main line; the branch shares the column of the step it hangs from.
  const cols = [];
  let col = -1;
  steps.forEach((s) => {
    if (s.id !== BRANCH) col += 1;
    cols.push(Math.max(0, col));
  });
  const stations = col + 1;
  const activeCol = cols[active] || 0;
  const progress = stations > 1 ? activeCol / (stations - 1) : 0;
  const railInset = `calc(100% / ${stations * 2})`;

  const select = (i, focus = false) => {
    setAuto(false);
    setActive(i);
    if (focus && tabRefs.current[i]) tabRefs.current[i].focus();
  };

  const onKeyDown = (e) => {
    const forward = isHe ? 'ArrowLeft' : 'ArrowRight';
    const back = isHe ? 'ArrowRight' : 'ArrowLeft';
    let next = null;
    if (e.key === forward) next = Math.min(last, active + 1);
    else if (e.key === back) next = Math.max(0, active - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    select(next, true);
  };

  const replay = () => {
    setActive(0);
    setAuto(true);
  };

  const current = steps[active];
  if (!current) return null;

  return (
    <section aria-labelledby="process-title" className="relative isolate overflow-hidden bg-navy-900 py-24 text-white sm:py-32">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[linear-gradient(rgba(255,255,255,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.035)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]"
      />
      <div aria-hidden="true" className="drift absolute -top-48 end-[-6rem] -z-10 h-[34rem] w-[34rem] rounded-full bg-grovv/15 blur-3xl" />

      <div className="wrap">
        <div className="max-w-3xl">
          <h2 id="process-title" className="heading text-[2.25rem] sm:text-5xl lg:text-6xl">
            <WordsReveal text={p('title')} />
          </h2>
          <Reveal as="p" delay={120} className="mt-6 max-w-[62ch] text-lg leading-relaxed text-navy-200 sm:text-xl">
            {p('lead')}
          </Reveal>
        </div>

        <Reveal delay={160} className="mt-16">
          <div ref={mapRef}>
            {/* Desktop: a line of stations with the order travelling along it */}
            <div className="hidden lg:block">
              <div
                role="tablist"
                aria-label={p('tablist')}
                onKeyDown={onKeyDown}
                className="relative grid"
                style={{ gridTemplateColumns: `repeat(${stations}, minmax(0, 1fr))` }}
              >
                <div aria-hidden="true" className="pointer-events-none relative h-5 self-start" style={{ gridColumn: '1 / -1', gridRow: 2 }}>
                  <span className="absolute top-[9px] h-0.5 rounded-full bg-white/15" style={{ insetInlineStart: railInset, insetInlineEnd: railInset }} />
                  <span
                    className="pm-fill absolute top-[9px] h-0.5 rounded-full bg-grovv"
                    style={{ insetInlineStart: railInset, insetInlineEnd: railInset, transform: `scaleX(${progress})` }}
                  />
                  <span
                    className="pm-token absolute inset-y-0 start-0 z-20 flex items-center justify-center"
                    style={{ width: `calc(100% / ${stations})`, transform: `translateX(calc(${activeCol} * var(--pm-dir) * 100%))` }}
                  >
                    <span className={`block h-5 w-5 rounded-full bg-grovv ring-4 ring-grovv/25 ${reduced ? '' : 'pm-pulse'}`} />
                  </span>
                </div>

                {steps.map((s, i) => {
                  const branch = s.id === BRANCH;
                  const isActive = i === active;
                  const done = i < active;
                  return (
                    <button
                      key={s.id}
                      ref={(el) => {
                        tabRefs.current[i] = el;
                      }}
                      type="button"
                      role="tab"
                      id={`pm-tab-${s.id}`}
                      aria-selected={isActive}
                      aria-controls="pm-panel"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => select(i)}
                      style={{ gridColumn: cols[i] + 1, gridRow: branch ? 1 : 2 }}
                      className={`group relative z-10 flex flex-col items-center rounded-xl px-1.5 text-center outline-none focus-visible:ring-2 focus-visible:ring-grovv-300 ${
                        branch ? 'self-end' : 'self-start'
                      }`}
                    >
                      {branch ? (
                        <>
                          <span
                            className={`mb-2.5 text-[0.95rem] font-medium leading-snug transition-colors ${
                              isActive ? 'text-white' : 'text-navy-300 group-hover:text-white'
                            }`}
                          >
                            {s.name}
                          </span>
                          <Dot active={isActive} done={done} branch />
                          <span
                            aria-hidden="true"
                            className={`block h-9 border-s-2 border-dashed transition-colors duration-500 ${isActive ? 'border-grovv' : 'border-white/25'}`}
                          />
                        </>
                      ) : (
                        <>
                          <Dot active={isActive} done={done} />
                          <span
                            className={`mt-4 text-[0.95rem] font-medium leading-snug transition-colors ${
                              isActive ? 'text-white' : done ? 'text-navy-200' : 'text-navy-300 group-hover:text-white'
                            }`}
                          >
                            {s.name}
                          </span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 flex items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[.03] px-5 py-3 text-[0.95rem] text-navy-200">
                {p('rail')}
              </div>

              <div id="pm-panel" role="tabpanel" aria-labelledby={`pm-tab-${current.id}`} className="mt-8 rounded-4xl bg-white/[.06] p-8 ring-1 ring-white/10 sm:p-10">
                <div className="flex flex-wrap items-center gap-3">
                  <span dir="ltr" className="font-mono text-[12px] text-navy-300">
                    order 1000452
                  </span>
                  <span className="rounded-full bg-grovv/15 px-3 py-1 text-[0.9rem] font-medium text-grovv-300">{current.object}</span>
                  <span className="ms-auto text-sm tabular-nums text-navy-300">
                    {active + 1} / {steps.length}
                  </span>
                </div>
                <div className="mt-5 grid">
                  {steps.map((s, i) => (
                    <div key={s.id} className={`col-start-1 row-start-1 ${i === active ? 'pm-insight' : 'invisible'}`}>
                      <div className="text-[0.95rem] font-semibold text-grovv-300">{s.name}</div>
                      <p className="mt-2 max-w-[48ch] text-2xl leading-snug text-white">{s.insight}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
                  <span className="text-sm text-navy-300">{t('site.illustrative')}</span>
                  {!auto && !reduced && (
                    <button
                      type="button"
                      onClick={replay}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-4 text-[0.95rem] font-medium text-white ring-1 ring-inset ring-white/20 transition-colors hover:bg-white/10"
                    >
                      <svg className="h-4 w-4 rtl:-scale-x-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <path d="M3 4v5h5" />
                      </svg>
                      {p('replay')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Phones: every step stays readable; the highlight and the line follow the order */}
            <ol className="lg:hidden">
              {steps.map((s, i) => {
                const isActive = i === active;
                return (
                  <li key={s.id} className="relative py-4 ps-10">
                    {i < last && (
                      <>
                        <span aria-hidden="true" className="absolute -bottom-[1.875rem] start-[9px] top-[1.875rem] w-0.5 bg-white/15" />
                        <span
                          aria-hidden="true"
                          className="pm-fill-y absolute -bottom-[1.875rem] start-[9px] top-[1.875rem] w-0.5 bg-grovv"
                          style={{ transform: `scaleY(${i < active ? 1 : 0})` }}
                        />
                      </>
                    )}
                    <span className="absolute start-0 top-5">
                      <Dot active={isActive} done={i < active} branch={s.id === BRANCH} />
                    </span>
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                      <span className={`text-lg font-semibold transition-colors ${isActive ? 'text-white' : 'text-navy-200'}`}>
                        {s.id === BRANCH ? <span aria-hidden="true">↳ </span> : null}
                        {s.name}
                      </span>
                      <span className="rounded-full bg-grovv/15 px-2.5 py-0.5 text-[0.85rem] font-medium text-grovv-300">{s.object}</span>
                    </div>
                    <p className={`mt-2 text-[1.02rem] leading-relaxed transition-colors ${isActive ? 'text-white' : 'text-navy-300'}`}>{s.insight}</p>
                  </li>
                );
              })}
            </ol>
            <p className="mt-4 rounded-2xl border border-dashed border-white/15 bg-white/[.03] px-4 py-3 text-[0.95rem] text-navy-200 lg:hidden">{p('rail')}</p>
          </div>
        </Reveal>

        <Reveal as="p" className="mt-10 max-w-[70ch] text-[0.95rem] leading-relaxed text-navy-300">
          {p('areas')}
        </Reveal>
      </div>
    </section>
  );
}
